import React, { useState, useRef, useEffect } from 'react'
import { useClickOutside } from '@/shared'
import styles from './TimePicker.module.scss'

export const TimePicker = ({
	value = '',
	format = 'HH:mm',
	onChange = () => {},
	placeholder = 'HH:mm',
	disabled = false,
	minuteStep = 1, // Интервал в минутах (по умолчанию 1)
	minTime = '', // Минимальное время для выбора
														 closeOnHourSelect = false, //закрытие по выбору часа
	...props
}) => {
	const [inputValue, setInputValue] = useState(value || '')
	const [isOpen, setIsOpen] = useState(false)
	const timePickerRef = useRef(null)

	useEffect(() => {
		setInputValue(value || '')
	}, [value])

	const toggleDropdown = () => {
		if (!disabled) {
			// Переключаем состояние отображения выпадающего списка
			const newIsOpen = !isOpen
			setIsOpen(newIsOpen)

			// Если открываем выпадающий список, прокручиваем к выбранным элементам после рендеринга
			if (newIsOpen) {
				setTimeout(() => {
					const selectedHourEl = document.querySelector(`.${styles.column} .${styles.selected}`)
					if (selectedHourEl) {
						selectedHourEl.scrollIntoView({ block: 'center' })
					}
				}, 0)
			}
		}
	}

	const handleInputChange = (e) => {
		let newValue = e.target.value
		// Разрешаем только цифры и двоеточие
		newValue = newValue.replace(/[^0-9:]/g, '')

		// Автоматически добавляем двоеточие после ввода часов
		if (newValue.length === 2 && !newValue.includes(':')) {
			newValue += ':'
		}

		// Проверяем формат и ограничения времени
		if (newValue.length <= 5) {
			setInputValue(newValue)

			// Если формат соответствует HH:mm, проверяем относительно minTime
			if (/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(newValue)) {
				// Проверяем ограничение по minTime
				let isValid = true

				if (minTime) {
					const [hour, minute] = newValue.split(':').map((part) => parseInt(part, 10))
					const [minHour, minMinute] = minTime.split(':').map((part) => parseInt(part, 10) || 0)

					// Недопустимо, если час меньше минимального или час равен минимальному, но минуты меньше
					if (hour < minHour || (hour === minHour && minute < minMinute)) {
						isValid = false
					}
				}

				// Применяем изменения только если время валидно
				if (isValid) {
					onChange(newValue)
				} else {
					// При невалидном значении можно установить minTime
					setInputValue(minTime)
					onChange(minTime)
				}
			}
		}
	}

	// Генерируем список часов и минут для выбора
	const renderTimeOptions = () => {
		// Определяем минимальные часы и минуты для ограничения выбора
		let minHour = 0
		let minMinute = 0

		if (minTime) {
			const [minTimeHour, minTimeMinute] = minTime.split(':').map((part) => parseInt(part, 10))
			minHour = minTimeHour || 0
			minMinute = minTimeMinute || 0
		}

		// Текущие часы и минуты из inputValue
		let [currentHour, currentMinute] = inputValue.split(':')
		currentHour = currentHour || '00'
		currentMinute = currentMinute || '00'

		// Генерируем список часов с учетом ограничения - фильтруем неактивные часы
		const allHours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
		const hours = minTime ? allHours.filter((hour) => parseInt(hour, 10) >= minHour) : allHours

		// Генерируем минуты с учетом интервала - фильтруем неактивные минуты
		const allMinutes = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) =>
			(i * minuteStep).toString().padStart(2, '0')
		)
		const minutes = allMinutes.filter((minute) => {
			// Если выбран минимальный час, отфильтруем минуты меньше минимальной
			if (minTime && parseInt(currentHour, 10) === minHour) {
				return parseInt(minute, 10) >= minMinute
			}
			return true
		})

		return (
			<div className={styles.timeOptions}>
				<div className={styles.column}>
					{hours.map((hour) => {
						const hourNum = parseInt(hour, 10)

						return (
							<div
								key={`hour-${hour}`}
								className={`${styles.option} ${currentHour === hour ? styles.selected : ''}`}
								onClick={() => {
									// Если выбирается минимальный час, проверяем какие минуты доступны
									if (minTime && hourNum === minHour) {
										// Получаем минимальную минуту
										const [, minTimeMinute] = minTime.split(':').map((part) => parseInt(part, 10) || 0)
										// Если текущая минута меньше минимальной, используем минимальную минуту
										const minuteToUse =
											parseInt(currentMinute, 10) < minTimeMinute
												? minTimeMinute.toString().padStart(2, '0')
												: currentMinute
										selectTime(hour, minuteToUse, closeOnHourSelect)
									} else {
										selectTime(hour, currentMinute, closeOnHourSelect)
									}
								}}
							>
								{hour}
							</div>
						)
					})}
				</div>
				<div className={styles.divider}>:</div>
				<div className={styles.column}>
					{minutes.map((minute) => {
						return (
							<div
								key={`minute-${minute}`}
								className={`${styles.option} ${currentMinute === minute ? styles.selected : ''}`}
								onClick={() => selectTime(currentHour, minute, true)} // Минуты всегда закрывают
							>
								{minute}
							</div>
						)
					})}
				</div>
			</div>
		)
	}

	const selectTime = (hour, minute, shouldClose = true) => {
		// Преобразуем значения в числа для сравнения
		const hourNum = parseInt(hour, 10)
		const minuteNum = parseInt(minute, 10)

		// Все допустимые значения уже отфильтрованы, поэтому просто применяем выбранное время
		// Дополнительная проверка для страховки (но она не должна срабатывать из-за фильтрации)
		if (minTime) {
			const [minHour, minMinute] = minTime.split(':').map((part) => parseInt(part, 10) || 0)

			// Если случайно выбрано недопустимое время, устанавливаем minTime
			if (hourNum < minHour || (hourNum === minHour && minuteNum < minMinute)) {
				setInputValue(minTime)
				onChange(minTime)
				if (shouldClose) setIsOpen(false)
				return
			}
		}

		// Применяем новое значение
		const formattedHour = hour.toString().padStart(2, '0')
		const formattedMinute = minute.toString().padStart(2, '0')
		const newValue = `${formattedHour}:${formattedMinute}`
		setInputValue(newValue)
		onChange(newValue)
		if (shouldClose) setIsOpen(false)
	}

	const handleClickOutside = () => {
		setIsOpen(false)
	}

	useClickOutside(timePickerRef, isOpen, handleClickOutside)

	return (
		<div className={styles.timePicker} ref={timePickerRef}>
			<input
				type="text"
				className={styles.input}
				value={inputValue}
				onChange={handleInputChange}
				onClick={toggleDropdown}
				placeholder={placeholder}
				disabled={disabled}
				{...props}
			/>
			{isOpen && <div className={styles.dropdown}>{renderTimeOptions()}</div>}
		</div>
	)
}
